import type { CSSProperties, ReactNode } from "react";

/**
 * A single-select strip of pills.
 *
 * The participant switcher on the schedule is the first control on the screen rather than a setting
 * somewhere else, because a member with two participants has two schedules and the switch between them
 * is the most common thing they do on that screen.
 *
 * The strip scrolls sideways by default instead of wrapping. Wrapping pushes the list down by a
 * whole row the moment a third child is added, and the row it pushes down is the content.
 *
 * `wrap` turns that off, and it is what every picker inside a sheet uses. A switcher is a filter,
 * so an option out of sight costs a swipe. A picker is a decision, and an option out of sight is an
 * option that does not exist: the owner has to be able to see the six shifts, or the seven days,
 * next to each other and next to the one they have already chosen.
 */
/**
 * One pill. An option with a `color` is a person with a mark colour (the member home's
 * participants): its border and words are that colour, and when chosen it is a raised face in
 * that colour (the `.sk-tile` recipe) with white words. Pass a `markColor`, which passes WCAG AA under
 * white text and on white. An option with no colour is charcoal when chosen (`.sk-chip-on`).
 */
export type ChipOption = {
  id: string;
  label: string;
  count?: number;
  color?: string;
  /** A small picture before the label: a business's mark in the member's business chips. */
  icon?: ReactNode;
};

export function ChipRow({
  options,
  selectedId,
  onSelect,
  label,
  wrap = false,
}: {
  options: ChipOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  label: string;
  wrap?: boolean;
}) {
  if (options.length <= 1) return null;

  return (
    <fieldset
      className={
        wrap ? "min-w-0 border-0 p-0" : "scroll-x -mx-5 min-w-0 border-0 p-0 px-5"
      }
    >
      <legend className="sr-only">{label}</legend>
      <div className={wrap ? "flex flex-wrap gap-2" : "flex w-max gap-2"}>
        {options.map((option) => {
          const active = option.id === selectedId;
          const tint = option.color
            ? active
              ? ({ "--sk-p": option.color, borderColor: "transparent", color: "var(--color-inverse)" } as CSSProperties)
              : { borderColor: option.color, color: option.color }
            : undefined;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(option.id)}
              style={tint}
              className={[
                "inline-flex items-center gap-1.5 rounded-pill border px-3.5",
                // Taller when wrapped, because a wrapped row is a picker being aimed at rather than
                // a filter being swiped past.
                wrap ? "h-11" : "h-9",
                "text-[13px] font-semibold whitespace-nowrap transition-colors duration-150",
                active ? (option.color ? "sk-tile" : "sk-chip-on") : "sk-chip text-ink-2 active:brightness-95",
              ].join(" ")}
            >
              {option.icon ? <span className={wrap ? "-ml-1.5" : "-ml-2"}>{option.icon}</span> : null}
              {option.label}
              {typeof option.count === "number" ? (
                <span className={active ? "text-inverse/70" : "text-ink-3"}>{option.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
